import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  writeBatch,
  serverTimestamp,
  Timestamp,
  increment,
  orderBy,
  setDoc,
  getDoc,
  arrayUnion,
  deleteDoc,
} from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../../../firebase";
import {
  TEAM_CATEGORIES,
  FABRIC_CATEGORIES,
  TRADE_ROLES,
  COUNTRIES,
} from "../../../data/constants";
import styles from "../../../styles/AdminConsole.module.css";
import { useToast } from "../shared/hooks/useToast";
import { useDragScroll } from "../shared/hooks/useDragScroll";
import StatCard from "../shared/components/StatCard";
import SelectWrapper from "../shared/components/SelectWrapper";
import Pagination from "../shared/components/Pagination";
import LeadTable from "./LeadTable";
import AddLeadModal from "./modals/AddLeadModal";
import EditLeadModal from "./modals/EditLeadModal";
import EmailsPanel from "./modals/EmailsPanel";
import LogContactModal from "./modals/LogContactModal";
import ContactLogViewer from "./modals/ContactLogViewer";
import SampleLogModal from "./modals/SampleLogModal";

// ── Constants ────────────────────────────────────────────────────────────────
const LOGO_URL =
  "https://cdn.synaptexglobal.com/Home%20(3240%20x%201080%20px).png";
const ITEMS_PER_PAGE = 15;
const AUTO_APPROVE_MS = 10 * 60 * 1000;

const EMPTY_FORM = {
  buyerName: "",
  contactPerson: "",
  country: "",
  fabricCategory: "knitted",
  tradeRole: "retail",
  buyerEmails: [],
};
const EMPTY_EDIT = {
  buyerName: "",
  contactPerson: "",
  country: "",
  fabricCategory: "knitted",
  tradeRole: "retail",
  buyerEmails: [],
  meetingDate: "",
  meetingLocation: "",
};

function tokenize(str) {
  return (str || "")
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 3);
}
function isSimilar(a, b) {
  const la = (a || "").toLowerCase(),
    lb = (b || "").toLowerCase();
  if (la.includes(lb) || lb.includes(la)) return true;
  return tokenize(a).filter((t) => tokenize(b).includes(t)).length >= 2;
}

// ── Component ────────────────────────────────────────────────────────────────
export default function LeadManagement() {
  const { uid: routeUid } = useParams();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const { scrollRef, onMouseDown, onMouseMove, onMouseUp } = useDragScroll();

  // ── Auth ──────────────────────────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [leads, setLeads] = useState([]);
  const [leadIndex, setLeadIndex] = useState([]);
  const [supplierUsers, setSupplierUsers] = useState([]);

  // ── Filters / pagination ──────────────────────────────────────────────────
  const [filterFabric, setFilterFabric] = useState("all");
  const [filterTrade, setFilterTrade] = useState("all");
  const [filterStatus, setFilterStatus] = useState("approved");
  const [currentPage, setCurrentPage] = useState(1);

  // ── Add Lead modal ────────────────────────────────────────────────────────
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [emailInput, setEmailInput] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Edit Lead modal ───────────────────────────────────────────────────────
  const [editingLead, setEditingLead] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editEmailInput, setEditEmailInput] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // ── Emails panel ──────────────────────────────────────────────────────────
  const [emailsEditLead, setEmailsEditLead] = useState(null);
  const [emailsPanelInput, setEmailsPanelInput] = useState("");
  const [emailsPanelList, setEmailsPanelList] = useState([]);
  const [emailsPanelSaving, setEmailsPanelSaving] = useState(false);

  // ── Log Contact modal ─────────────────────────────────────────────────────
  const [logContactLead, setLogContactLead] = useState(null);
  const [logDate, setLogDate] = useState("");
  const [logNote, setLogNote] = useState("");
  const [logSaving, setLogSaving] = useState(false);

  // ── Contact Log viewer ────────────────────────────────────────────────────
  const [contactLogLead, setContactLogLead] = useState(null);

  // ── Sample Log modal ──────────────────────────────────────────────────────
  const [sampleLogLead, setSampleLogLead] = useState(null);
  const [sampleLogDate, setSampleLogDate] = useState("");
  const [sampleLogNote, setSampleLogNote] = useState("");
  const [sampleLogSaving, setSampleLogSaving] = useState(false);

  // ── Inline meeting edit ───────────────────────────────────────────────────
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  const [meetingDateInput, setMeetingDateInput] = useState("");
  const [meetingLocationInput, setMeetingLocationInput] = useState("");

  // ── Delete confirm ────────────────────────────────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // ── Route owner (the merchandiser whose leads page we're on) ──────────────
  const [routeOwnerProfile, setRouteOwnerProfile] = useState(null);

  const isAdmin = userProfile?.isAdmin === true;
  const isMerchandiser = userProfile?.category === "merchandiser";
  const canWrite = isAdmin || isMerchandiser;

  // ── Auth listener ─────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      const data = snap.data() || {};
      if (!data.isAdmin && user.uid !== routeUid) {
        navigate("/leads/" + user.uid);
        return;
      }
      setCurrentUser(user);
      setUserProfile({ uid: user.uid, ...data });
      setAuthReady(true);
    });
    return () => unsub();
  }, [routeUid, navigate]);

  // ── Route owner profile ───────────────────────────────────────────────────
  useEffect(() => {
    if (!authReady) return;
    if (routeUid === currentUser?.uid) {
      setRouteOwnerProfile(userProfile);
      return;
    }
    getDoc(doc(db, "users", routeUid)).then((snap) => {
      if (snap.exists())
        setRouteOwnerProfile({ uid: routeUid, ...snap.data() });
    });
  }, [authReady, routeUid, currentUser, userProfile]);

  // ── Live leads ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authReady) return;
    const q = query(
      collection(db, "users", routeUid, "leads"),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(q, (snap) =>
      setLeads(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }, [authReady, routeUid]);

  // ── Lead index (approved only — for duplicate detection) ──────────────────
  useEffect(() => {
    if (!authReady) return;
    const q = query(
      collection(db, "leadIndex"),
      where("status", "==", "approved"),
    );
    return onSnapshot(q, (snap) =>
      setLeadIndex(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }, [authReady]);

  // ── Supplier users (admin only) ───────────────────────────────────────────
  useEffect(() => {
    if (!authReady || !isAdmin) return;
    const q = query(collection(db, "users"), where("isAdmin", "==", false));
    return onSnapshot(q, (snap) => {
      const all = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      setSupplierUsers(
        all.filter((u) => !TEAM_CATEGORIES.includes(u.category)),
      );
    });
  }, [authReady, isAdmin]);

  // ── Auto-approve sweep ────────────────────────────────────────────────────
  useEffect(() => {
    if (!leads.length) return;
    const now = new Date();
    const stale = leads.filter(
      (l) => l.status === "pending" && l.autoApproveAt?.toDate() <= now,
    );
    if (!stale.length) return;
    const batch = writeBatch(db);
    stale.forEach((l) => {
      batch.update(doc(db, "users", routeUid, "leads", l.id), {
        status: "approved",
        autoApproveAt: null,
      });
      batch.update(doc(db, "leadIndex", l.id), {
        status: "approved",
        autoApproveAt: null,
      });
    });
    batch.commit().catch(console.error);
  }, [leads, routeUid]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const filtered = useMemo(
    () =>
      leads.filter((l) => {
        if (filterFabric !== "all" && l.fabricCategory !== filterFabric)
          return false;
        if (filterTrade !== "all" && l.tradeRole !== filterTrade) return false;
        if (filterStatus !== "all" && l.status !== filterStatus) return false;
        return true;
      }),
    [leads, filterFabric, filterTrade, filterStatus],
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = useMemo(
    () =>
      filtered.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
      ),
    [filtered, currentPage],
  );

  const stats = useMemo(() => {
    const approved = leads.filter((l) => l.status === "approved");
    return {
      total: leads.length,
      active: approved.filter((l) => !l.orderConverted).length,
      meetings: approved.filter((l) => l.meetingConfirmed).length,
      samples: approved.filter((l) => l.sampleStatus !== null).length,
      converted: approved.filter((l) => l.orderConverted).length,
      awaiting: leads.filter(
        (l) => l.status === "pending" || l.status === "flagged",
      ).length,
    };
  }, [leads]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAddLead = useCallback(async () => {
    if (!form.buyerName.trim() || !form.contactPerson.trim() || !form.country) {
      showToast("Please fill in all required fields.", "error");
      return;
    }
    setSaving(true);
    try {
      const conflicts = leadIndex.filter(
        (l) =>
          l.ownerUid !== routeUid && isSimilar(form.buyerName, l.buyerName),
      );
      const status = conflicts.length > 0 ? "flagged" : "pending";
      const autoApproveAt =
        conflicts.length === 0
          ? Timestamp.fromDate(new Date(Date.now() + AUTO_APPROVE_MS))
          : null;
      const flaggedMatches = conflicts.map((c) => ({
        leadId: c.id,
        ownerUid: c.ownerUid,
        ownerName: c.ownerName,
        buyerName: c.buyerName,
      }));
      const payload = {
        ...form,
        tryouts: 0,
        lastContactDate: null,
        createdByUid: currentUser.uid,
        createdByName: userProfile?.name,
        contactLog: [],
        meetingConfirmed: false,
        meetingDone: false,
        meetingDate: null,
        meetingLocation: null,
        meetingPaymentPaid: false,
        sampleStatus: null,
        sampleRequestDate: null,
        sampleApprovedDate: null,
        orderConverted: false,
        orderPaid: false,
        orderPaymentPaid: false,
        potentialSupplierUid: null,
        deleteRequested: false,
        status,
        autoApproveAt,
        flaggedMatches,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(
        collection(db, "users", routeUid, "leads"),
        payload,
      );
      await setDoc(doc(db, "leadIndex", docRef.id), {
        buyerName: payload.buyerName,
        country: payload.country,
        ownerUid: routeUid,
        ownerName: routeOwnerProfile?.name, // merchandiser's name (route owner)
        createdByUid: currentUser.uid,
        createdByName: userProfile?.name, // logged-in user's name
        status,
        orderConverted: false,
        deleteRequested: false,
        autoApproveAt,
        flaggedMatches,
        createdAt: serverTimestamp(),
      });
      showToast(
        status === "flagged"
          ? "Lead submitted — flagged for admin review."
          : "Lead submitted — approval in less than 10 minutes.",
      );
      setForm(EMPTY_FORM);
      setEmailInput("");
      setShowAddModal(false);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    leadIndex,
    routeUid,
    currentUser,
    userProfile,
    routeOwnerProfile,
    showToast,
  ]);

  const handleOpenEdit = useCallback((lead) => {
    setEditingLead(lead);
    setEditForm({
      buyerName: lead.buyerName || "",
      contactPerson: lead.contactPerson || "",
      country: lead.country || "",
      fabricCategory: lead.fabricCategory || "knitted",
      tradeRole: lead.tradeRole || "retail",
      buyerEmails: lead.buyerEmails || [],
      meetingDate: lead.meetingDate
        ? lead.meetingDate.toDate().toISOString().split("T")[0]
        : "",
      meetingLocation: lead.meetingLocation || "",
    });
    setEditEmailInput("");
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingLead) return;
    setEditSaving(true);
    try {
      const updates = {
        buyerName: editForm.buyerName.trim(),
        contactPerson: editForm.contactPerson.trim(),
        country: editForm.country,
        buyerEmails: editForm.buyerEmails,
        fabricCategory: editForm.fabricCategory,
        tradeRole: editForm.tradeRole,
        meetingDate: editForm.meetingDate
          ? Timestamp.fromDate(new Date(editForm.meetingDate))
          : null,
        meetingLocation: editForm.meetingLocation.trim() || null,
      };
      await updateDoc(
        doc(db, "users", routeUid, "leads", editingLead.id),
        updates,
      );
      await updateDoc(doc(db, "leadIndex", editingLead.id), {
        buyerName: updates.buyerName,
        country: updates.country,
      });
      showToast("Lead updated.");
      setEditingLead(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setEditSaving(false);
    }
  }, [editingLead, editForm, routeUid, showToast]);

  const handleOpenEmailsPanel = useCallback((lead) => {
    setEmailsEditLead(lead);
    setEmailsPanelList(lead.buyerEmails || []);
    setEmailsPanelInput("");
  }, []);

  const handleSaveEmails = useCallback(async () => {
    if (!emailsEditLead) return;
    setEmailsPanelSaving(true);
    try {
      await updateDoc(doc(db, "users", routeUid, "leads", emailsEditLead.id), {
        buyerEmails: emailsPanelList,
      });
      showToast("Emails updated.");
      setEmailsEditLead(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setEmailsPanelSaving(false);
    }
  }, [emailsEditLead, emailsPanelList, routeUid, showToast]);

  const handleOpenLogContact = useCallback((lead) => {
    setLogContactLead(lead);
    setLogDate(new Date().toISOString().split("T")[0]);
    setLogNote("");
  }, []);

  const handleSaveLog = useCallback(async () => {
    if (!logContactLead || !logDate) {
      showToast("Date is required.", "error");
      return;
    }
    setLogSaving(true);
    try {
      const ts = Timestamp.fromDate(new Date(logDate));
      await updateDoc(doc(db, "users", routeUid, "leads", logContactLead.id), {
        tryouts: increment(1),
        lastContactDate: ts,
        contactLog: arrayUnion({ date: ts, note: logNote.trim() }),
      });
      showToast("Contact logged.");
      setLogContactLead(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLogSaving(false);
    }
  }, [logContactLead, logDate, logNote, routeUid, showToast]);

  const handleSaveSampleLog = useCallback(async () => {
    if (!sampleLogLead || !sampleLogDate) {
      showToast("Date is required.", "error");
      return;
    }
    setSampleLogSaving(true);
    try {
      await updateDoc(doc(db, "users", routeUid, "leads", sampleLogLead.id), {
        sampleLog: arrayUnion({
          date: Timestamp.fromDate(new Date(sampleLogDate)),
          note: sampleLogNote.trim(),
          stage: sampleLogLead.sampleStatus,
        }),
      });
      showToast("Sample log saved.");
      setSampleLogNote("");
      setSampleLogLead(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSampleLogSaving(false);
    }
  }, [sampleLogLead, sampleLogDate, sampleLogNote, routeUid, showToast]);

  const toggleField = useCallback(
    async (leadId, field, currentVal, extra = {}) => {
      if (!canWrite) return;
      const newVal = !currentVal;
      await updateDoc(doc(db, "users", routeUid, "leads", leadId), {
        [field]: newVal,
        ...extra,
      });
      if (field === "orderConverted" || field === "deleteRequested") {
        await updateDoc(doc(db, "leadIndex", leadId), { [field]: newVal });
      }
    },
    [canWrite, routeUid],
  );

  const handleSampleChange = useCallback(
    async (lead, newStatus) => {
      const updates = { sampleStatus: newStatus || null };
      if (newStatus === "requested" && !lead.sampleRequestDate)
        updates.sampleRequestDate = Timestamp.now();
      if (newStatus === "approved")
        updates.sampleApprovedDate = Timestamp.now();
      await updateDoc(doc(db, "users", routeUid, "leads", lead.id), updates);
    },
    [routeUid],
  );

  const saveMeetingDetails = useCallback(
    async (leadId) => {
      await updateDoc(doc(db, "users", routeUid, "leads", leadId), {
        meetingDate: meetingDateInput
          ? Timestamp.fromDate(new Date(meetingDateInput))
          : null,
        meetingLocation: meetingLocationInput.trim() || null,
      });
      setEditingMeetingId(null);
    },
    [routeUid, meetingDateInput, meetingLocationInput],
  );

  const handleSetEditingMeeting = useCallback((lead) => {
    setEditingMeetingId(lead.id);
    setMeetingDateInput(
      lead.meetingDate
        ? lead.meetingDate.toDate().toISOString().split("T")[0]
        : "",
    );
    setMeetingLocationInput(lead.meetingLocation || "");
  }, []);

  const assignSupplier = useCallback(
    async (leadId, supplierUid) => {
      await updateDoc(doc(db, "users", routeUid, "leads", leadId), {
        potentialSupplierUid: supplierUid || null,
      });
    },
    [routeUid],
  );

  const handleApprove = useCallback(
    async (lead) => {
      const batch = writeBatch(db);
      batch.update(doc(db, "users", routeUid, "leads", lead.id), {
        status: "approved",
        autoApproveAt: null,
        flaggedMatches: [],
      });
      batch.update(doc(db, "leadIndex", lead.id), {
        status: "approved",
        autoApproveAt: null,
        flaggedMatches: [],
      });
      await batch.commit();
      showToast("Lead approved.");
    },
    [routeUid, showToast],
  );

  const handleReject = useCallback(
    async (lead) => {
      const batch = writeBatch(db);
      batch.update(doc(db, "users", routeUid, "leads", lead.id), {
        status: "rejected",
        autoApproveAt: null,
      });
      batch.update(doc(db, "leadIndex", lead.id), {
        status: "rejected",
        autoApproveAt: null,
      });
      await batch.commit();
      showToast("Lead rejected.", "error");
    },
    [routeUid, showToast],
  );

  const handleDelete = useCallback(
    async (lead) => {
      if (isAdmin) {
        if (deleteConfirmId !== lead.id) {
          setDeleteConfirmId(lead.id);
          return;
        }
        const batch = writeBatch(db);
        batch.delete(doc(db, "users", routeUid, "leads", lead.id));
        batch.delete(doc(db, "leadIndex", lead.id));
        await batch.commit();
        setDeleteConfirmId(null);
        showToast(`"${lead.buyerName}" deleted.`, "error");
      } else {
        const batch = writeBatch(db);
        batch.update(doc(db, "users", routeUid, "leads", lead.id), {
          deleteRequested: true,
          previousStatus: lead.status,
          status: "delete",
        });
        batch.update(doc(db, "leadIndex", lead.id), {
          deleteRequested: true,
          previousStatus: lead.status,
          status: "delete",
        });
        await batch.commit();
        showToast("Deletion request sent to admin.");
      }
    },
    [isAdmin, deleteConfirmId, routeUid, showToast],
  );

  const handleCancelDelete = useCallback(
    async (lead) => {
      const restoreStatus = lead.previousStatus || "approved";
      const batch = writeBatch(db);
      batch.update(doc(db, "users", routeUid, "leads", lead.id), {
        deleteRequested: false,
        previousStatus: null,
        status: restoreStatus,
      });
      batch.update(doc(db, "leadIndex", lead.id), {
        deleteRequested: false,
        previousStatus: null,
        status: restoreStatus,
      });
      await batch.commit();
      showToast("Deletion request cancelled.");
    },
    [routeUid, showToast],
  );

  const handleOpenSampleLog = useCallback((lead) => {
    setSampleLogLead(lead);
    setSampleLogDate(new Date().toISOString().split("T")[0]);
    setSampleLogNote("");
  }, []);

  const handleLogout = useCallback(async () => {
    await signOut(auth);
    navigate("/");
  }, [navigate]);

  if (!authReady) return null;

  const isViewingOwn = currentUser?.uid === routeUid;

  return (
    <div className={styles.page}>
      {/* ── Top Bar ── */}
      <nav className={styles.topBar}>
        <a href="/" className={styles.topLogo}>
          <img src={LOGO_URL} alt="Synaptex" className={styles.logoImg} />
        </a>
        <div className={styles.navRight}>
          {isAdmin && (
            <button
              className={styles.navBtn}
              onClick={() => navigate("/adminconsole")}
            >
              Console
            </button>
          )}
          <button
            className={`${styles.navBtn} ${styles.navBtnActive}`}
            onClick={() => navigate(`/leads/${routeUid}`)}
          >
            Leads
          </button>
          <button className={styles.navBtn} onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </nav>

      {/* ── Welcome ── */}
      <div className={styles.welcomeBar}>
        <span className={styles.welcomeText}>
          {isViewingOwn ? "YOUR LEAD PIPELINE" : "VIEWING LEADS —"}{" "}
          {!isViewingOwn && (
            <span className={styles.welcomeName}>{userProfile?.name}</span>
          )}
        </span>
        <h1 className={styles.pageTitle}>Lead Management</h1>
      </div>

      <div className={styles.rule} />

      {/* ── Stats ── */}
      <div className={styles.statsRow}>
        {[
          { label: "Total Leads", value: stats.total, sub: "all entries" },
          { label: "Active", value: stats.active, sub: "not converted" },
          { label: "Meetings", value: stats.meetings, sub: "confirmed" },
          { label: "Samples", value: stats.samples, sub: "in pipeline" },
          { label: "Converted", value: stats.converted, sub: "orders won" },
          { label: "Awaiting", value: stats.awaiting, sub: "pending approval" },
        ].map((s) => (
          <StatCard key={s.label} label={s.label} value={s.value} sub={s.sub} />
        ))}
      </div>

      {/* ── Table wrapper ── */}
      <div className={styles.tableWrapper} style={{ maxWidth: "100%" }}>
        {/* Controls */}
        <div className={styles.tableControls}>
          <div className={styles.tableFilters}>
            <div className={styles.tableFilterGroup}>
              <span className={styles.tableFilterLabel}>Fabric</span>
              <SelectWrapper
                value={filterFabric}
                selectClassName={styles.tableFilterSelect}
                onChange={(e) => {
                  setFilterFabric(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                {FABRIC_CATEGORIES.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </SelectWrapper>
            </div>
            <div className={styles.tableFilterGroup}>
              <span className={styles.tableFilterLabel}>Role</span>
              <SelectWrapper
                value={filterTrade}
                selectClassName={styles.tableFilterSelect}
                onChange={(e) => {
                  setFilterTrade(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                {TRADE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </SelectWrapper>
            </div>
            <div className={styles.tableFilterGroup}>
              <span className={styles.tableFilterLabel}>Status</span>
              <SelectWrapper
                value={filterStatus}
                selectClassName={styles.tableFilterSelect}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="flagged">Flagged</option>
                <option value="rejected">Rejected</option>
                <option value="delete">Delete Requested</option>
              </SelectWrapper>
            </div>
          </div>
          {canWrite && (
            <button
              className={`${styles.btn} ${styles.btnPrimary} ${styles.tableAddBtn}`}
              onClick={() => setShowAddModal(true)}
            >
              + Add Lead
            </button>
          )}
        </div>

        {/* Table */}
        <LeadTable
          scrollRef={scrollRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          paginated={paginated}
          isAdmin={isAdmin}
          canWrite={canWrite}
          supplierUsers={supplierUsers}
          deleteConfirmId={deleteConfirmId}
          editingMeetingId={editingMeetingId}
          meetingDateInput={meetingDateInput}
          meetingLocationInput={meetingLocationInput}
          onToggleField={toggleField}
          onSampleChange={handleSampleChange}
          onOpenLogContact={handleOpenLogContact}
          onOpenSampleLog={handleOpenSampleLog}
          onOpenEmailsPanel={handleOpenEmailsPanel}
          onOpenEdit={handleOpenEdit}
          onDelete={handleDelete}
          onApprove={handleApprove}
          onReject={handleReject}
          onCancelDelete={handleCancelDelete}
          onAssignSupplier={assignSupplier}
          onViewContactLog={setContactLogLead}
          onSetEditingMeeting={handleSetEditingMeeting}
          onMeetingDateChange={setMeetingDateInput}
          onMeetingLocationChange={setMeetingLocationInput}
          onSaveMeeting={saveMeetingDetails}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* ── Modals — all rendered once, outside the table ── */}
      {showAddModal && (
        <AddLeadModal
          form={form}
          setForm={setForm}
          emailInput={emailInput}
          setEmailInput={setEmailInput}
          saving={saving}
          onSubmit={handleAddLead}
          onClose={() => {
            setShowAddModal(false);
            setForm(EMPTY_FORM);
            setEmailInput("");
          }}
        />
      )}

      {editingLead && (
        <EditLeadModal
          lead={editingLead}
          form={editForm}
          setForm={setEditForm}
          emailInput={editEmailInput}
          setEmailInput={setEditEmailInput}
          saving={editSaving}
          onSave={handleSaveEdit}
          onClose={() => setEditingLead(null)}
        />
      )}

      {emailsEditLead && (
        <EmailsPanel
          lead={emailsEditLead}
          list={emailsPanelList}
          inputValue={emailsPanelInput}
          onInputChange={(v) => setEmailsPanelInput(v)}
          onAdd={() => {
            const v = emailsPanelInput.trim().toLowerCase();
            if (!v.includes("@") || emailsPanelList.includes(v)) return;
            setEmailsPanelList((p) => [...p, v]);
            setEmailsPanelInput("");
          }}
          onRemove={(e) => setEmailsPanelList((p) => p.filter((x) => x !== e))}
          saving={emailsPanelSaving}
          onSave={handleSaveEmails}
          onClose={() => setEmailsEditLead(null)}
        />
      )}

      {logContactLead && (
        <LogContactModal
          lead={logContactLead}
          date={logDate}
          note={logNote}
          saving={logSaving}
          onDateChange={setLogDate}
          onNoteChange={setLogNote}
          onSave={handleSaveLog}
          onClose={() => setLogContactLead(null)}
        />
      )}

      {contactLogLead && (
        <ContactLogViewer
          lead={contactLogLead}
          onClose={() => setContactLogLead(null)}
        />
      )}

      {sampleLogLead && (
        <SampleLogModal
          lead={sampleLogLead}
          date={sampleLogDate}
          note={sampleLogNote}
          saving={sampleLogSaving}
          canWrite={canWrite}
          onDateChange={setSampleLogDate}
          onNoteChange={setSampleLogNote}
          onSave={handleSaveSampleLog}
          onClose={() => setSampleLogLead(null)}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={styles.toast}>
          <div
            className={
              toast.type === "error" ? styles.toastError : styles.toastSuccess
            }
          >
            {toast.text}
          </div>
        </div>
      )}
    </div>
  );
}
